import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, Check, ChevronRight, Loader2, PhoneOff } from 'lucide-react';
import { api } from '../../lib/api.js';
import { cn } from '../../lib/utils.js';

/**
 * Recording the outcome of a call that already ended. Item 27.
 *
 * WHY THIS IS A LIST AND NOT AN IN-CALL WIDGET, which is the whole design
 * question. A bridge call is answered on the operator's own phone: Twilio rings
 * their cell, they talk to the seller with the app nowhere in sight, and they hang
 * up. There is no tab to prompt in and no moment to interrupt. So the only place
 * a bridge disposition can be captured is afterwards, from a list of calls that
 * ended without one. A browser call could have been prompted in-tab; building
 * only that would have covered the easier half of item 27 and missed the half it
 * names.
 *
 * WHERE IT LIVES. At the top of the dialer, because opening the dialer is the
 * moment somebody is about to make the next call - which is exactly when item
 * 28's rule would stop them. The prompt and the block are in the same place, so
 * the way out of the block is the thing already on screen.
 *
 * THE PICKER IS THE CONFIGURED TREE, walked to its leaves. Categories organise
 * and cannot be chosen; only leaves can, because recording "Reached the
 * decision-maker" instead of "Warm" is how a taxonomy stops meaning anything.
 */

interface AwaitingCall {
  dealCallId: string;
  partyId: string | null;
  dealThreadId: string;
  endedAt: string | null;
  toNumber: string | null;
}

interface AwaitingResponse {
  calls: AwaitingCall[];
  requireBeforeAdvance: boolean;
}

interface SelectableDisposition {
  id: string;
  label: string;
  path: string[];
  pathLabel: string;
}

interface DispositionsResponse {
  selectable: SelectableDisposition[];
}

function shortTime(value: string | null): string {
  if (!value) return 'recently';
  const at = new Date(value);
  if (Number.isNaN(at.getTime())) return 'recently';
  const minutes = Math.round((Date.now() - at.getTime()) / 60000);
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.round(hours / 24)}d ago`;
}

export default function DispositionCapture({ open }: { open: boolean }) {
  const queryClient = useQueryClient();
  const [activeCallId, setActiveCallId] = useState<string | null>(null);
  const [note, setNote] = useState('');
  const [error, setError] = useState<string | null>(null);

  const awaiting = useQuery({
    queryKey: ['deal-calls', 'awaiting-disposition'],
    queryFn: () => api.get<AwaitingResponse>('/api/deal-calls/awaiting-disposition'),
    enabled: open,
    staleTime: 15_000,
  });

  const dispositions = useQuery({
    queryKey: ['dispositions'],
    queryFn: () => api.get<DispositionsResponse>('/api/dispositions'),
    enabled: open,
    staleTime: 300_000,
  });

  const record = useMutation({
    mutationFn: ({ callId, disposition }: { callId: string; disposition: string }) =>
      api.post(`/api/deal-calls/${callId}/disposition`, {
        disposition,
        ...(note.trim() ? { note: note.trim() } : {}),
      }),
    onSuccess: () => {
      setActiveCallId(null);
      setNote('');
      setError(null);
      void queryClient.invalidateQueries({ queryKey: ['deal-calls', 'awaiting-disposition'] });
    },
    onError: () => setError('That outcome could not be saved. Try again.'),
  });

  const calls = awaiting.data?.calls ?? [];
  const required = awaiting.data?.requireBeforeAdvance ?? false;

  /** Leaves grouped by their parent path, so the picker reads as the tree. */
  const grouped = useMemo(() => {
    const options = dispositions.data?.selectable ?? [];
    const byGroup = new Map<string, SelectableDisposition[]>();
    for (const option of options) {
      const groupLabel = option.path.length > 1 ? option.path.slice(0, -1).join(' > ') : '';
      const bucket = byGroup.get(groupLabel) ?? [];
      bucket.push(option);
      byGroup.set(groupLabel, bucket);
    }
    return [...byGroup.entries()];
  }, [dispositions.data]);

  if (!open) return null;
  if (awaiting.isLoading) {
    return (
      <div className="flex items-center gap-2 px-4 py-3 text-xs text-ink-subtle" data-testid="disposition-loading">
        <Loader2 size={13} className="animate-spin" /> Checking for calls that need an outcome
      </div>
    );
  }
  if (calls.length === 0) return null;

  return (
    <div
      className={cn(
        'border-b px-4 py-3',
        required ? 'border-warning/40 bg-warning/10' : 'border-line bg-surface-sunken/40',
      )}
      data-testid="disposition-capture"
    >
      <div className="mb-2 flex items-start gap-2">
        <span className={cn('mt-0.5 flex-shrink-0', required ? 'text-warning' : 'text-ink-muted')}>
          {required ? <AlertTriangle size={14} /> : <PhoneOff size={14} />}
        </span>
        <div className="min-w-0">
          <p className="text-xs font-semibold text-ink" data-testid="disposition-heading">
            {calls.length === 1
              ? 'One call still needs an outcome'
              : `${calls.length} calls still need an outcome`}
          </p>
          {/* Said only when it is true. A warning that appears whether or not it
              applies teaches people to ignore it. */}
          <p className="mt-0.5 text-[11px] text-ink-muted" data-testid="disposition-subheading">
            {required
              ? 'Record the outcome before calling this contact again.'
              : 'Recording it keeps the call history worth reading.'}
          </p>
        </div>
      </div>

      <div className="space-y-1.5">
        {calls.map((call) => {
          const isActive = activeCallId === call.dealCallId;
          return (
            <div
              key={call.dealCallId}
              className="rounded-lg border border-line bg-surface p-2.5"
              data-testid={`awaiting-call-${call.dealCallId}`}
            >
              <button
                type="button"
                onClick={() => {
                  setActiveCallId(isActive ? null : call.dealCallId);
                  setError(null);
                }}
                className="flex w-full items-center gap-2 text-left"
                data-testid={`awaiting-call-toggle-${call.dealCallId}`}
              >
                <ChevronRight
                  size={13}
                  className={cn('flex-shrink-0 text-ink-subtle transition-transform', isActive && 'rotate-90')}
                />
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-xs font-medium text-ink mask-sensitive">
                    {call.toNumber ?? 'Unknown number'}
                  </span>
                  <span className="block text-[11px] text-ink-subtle">Ended {shortTime(call.endedAt)}</span>
                </span>
                {!isActive && (
                  <span className="flex-shrink-0 text-[11px] font-medium text-brand">Record outcome</span>
                )}
              </button>

              {isActive && (
                <div className="mt-2.5 border-t border-line pt-2.5">
                  {dispositions.isLoading && (
                    <p className="flex items-center gap-1.5 text-[11px] text-ink-subtle">
                      <Loader2 size={12} className="animate-spin" /> Loading outcomes
                    </p>
                  )}

                  {dispositions.isError && (
                    <p className="text-[11px] text-danger" data-testid="disposition-options-error">
                      The configured outcomes could not be loaded, so there is nothing to pick yet.
                    </p>
                  )}

                  {grouped.map(([groupLabel, options]) => (
                    <div key={groupLabel || 'ungrouped'} className="mb-2">
                      {groupLabel && (
                        <p className="mb-1 text-[10px] font-semibold uppercase tracking-wide text-ink-subtle">
                          {groupLabel}
                        </p>
                      )}
                      <div className="flex flex-wrap gap-1.5">
                        {options.map((option) => (
                          <button
                            key={option.id}
                            type="button"
                            disabled={record.isPending}
                            title={option.pathLabel}
                            onClick={() => record.mutate({ callId: call.dealCallId, disposition: option.id })}
                            data-testid={`disposition-option-${option.id}`}
                            className={cn(
                              'inline-flex items-center gap-1 rounded-md border px-2 py-1 text-[11px] transition-colors',
                              'border-line bg-surface text-ink-muted hover:border-brand/30 hover:text-ink',
                              record.isPending && 'cursor-not-allowed opacity-50',
                            )}
                          >
                            {record.isPending && record.variables?.disposition === option.id
                              ? <Loader2 size={11} className="animate-spin" />
                              : <Check size={11} className="opacity-0" />}
                            {option.label}
                          </button>
                        ))}
                      </div>
                    </div>
                  ))}

                  <textarea
                    rows={2}
                    value={note}
                    maxLength={1000}
                    onChange={(event) => setNote(event.target.value)}
                    placeholder="Anything worth remembering, optional"
                    data-testid="disposition-note"
                    className="mt-1 w-full resize-y rounded-md border border-line bg-surface px-2 py-1.5 text-[11px] text-ink placeholder:text-ink-subtle focus:border-brand focus:outline-none"
                  />

                  {error && (
                    <p className="mt-1 text-[11px] text-danger" data-testid="disposition-error">{error}</p>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
